/**
 * THE page-load loader: the Flashka mark's two cards shuffle past each other
 * while the deck squashes on the crossing and recoils as they land ("Shuffle
 * V2", picked by the founder 2026-10-20 from the loading-states revamp).
 *
 * This file is the single source of truth for every renderer:
 *   - web      → `logo-loader-styles.ts` turns these tracks into CSS keyframes
 *                (inline SVG, no animation runtime, so it draws on first paint;
 *                the same CSS is inlined into flashka-web/index.html for boot);
 *   - native   → `LogoLoader.native.tsx` samples the same tracks on the UI
 *                thread from one looping clock, so every part stays in phase.
 *
 * Geometry is in the mark's own viewBox units (18.4947 × 20, from Logo.svg).
 * Every track starts and ends on the resting logo — that is what makes the
 * loop seamless — and `logo-loader.spec.ts` pins it.
 */

/** A cubic-bezier as CSS writes it: `cubic-bezier(x1, y1, x2, y2)`. */
export type LogoEase = readonly [number, number, number, number];

/** One keyframe: the values at `at` (0–1 of the loop), and the curve INTO the next one. */
export interface LogoKeyframe {
  at: number;
  values: readonly number[];
  ease?: LogoEase;
}

export type LogoTrack = readonly LogoKeyframe[];

/** The mark, in viewBox units. */
export const LOGO_GEOMETRY = {
  width: 18.4947,
  height: 20,
  frameRadius: 2.85714,
  card: { x: 3.22583, width: 13.5484, height: 7.44623, radius: 1.42857 },
  /** Top (amber) card's y; the bottom (violet) card's y. */
  cardAY: 1.72044,
  cardBY: 10.8333,
} as const;

/**
 * Brand-mark colours, the same values as Logo.svg / darkLogo.svg and
 * `ThemedLogo`: this is the logo's own art, not a UI surface, so it does not
 * follow the DS ramp. Dark is the monochrome mark (founder pick: "v2 mono").
 */
export const LOGO_LOADER_COLORS = {
  light: { frame: '#262626', cardA: '#FCD34D', cardB: '#A78BFA' },
  dark: { frame: '#D4D4D4', cardA: '#171717', cardB: '#171717' },
} as const;

/** One loop of the shuffle. */
export const LOGO_LOADER_DURATION_MS = 2400;

/**
 * The loader only appears after this much waiting, so a fast load never
 * flashes it (user-chosen waits can opt out with `immediate`).
 */
export const LOGO_LOADER_DELAY_MS = 300;
/** Its fade-in once the delay is over. */
export const LOGO_LOADER_FADE_IN_MS = 200;

/** Default curve of a segment that does not name its own. */
export const LOGO_EASE_DEFAULT: LogoEase = [0.45, 0, 0.55, 1];
const TRAVEL: LogoEase = [0.65, 0, 0.35, 1];
const SWING_OUT: LogoEase = [0.33, 1, 0.68, 1];
const SWING_IN: LogoEase = [0.32, 0, 0.67, 0];
const SMOOTH: LogoEase = [0.45, 0, 0.55, 1];

/** How far a card travels to take the other's slot. */
const SWAP = LOGO_GEOMETRY.cardBY - LOGO_GEOMETRY.cardAY;

/**
 * The tracks. Cards cross between 6% and 32% (amber in front, violet behind,
 * each bowing sideways), hold swapped, cross back between 50% and 76%, then
 * rest. The deck goes wide on each crossing and recoils tall as the cards land.
 */
export const LOGO_LOADER_TRACKS = {
  /** Deck (frame + cards): [scaleX, scaleY], pivoting on the frame's bottom centre. */
  deck: [
    { at: 0, values: [1, 1] },
    { at: 0.06, values: [1, 1], ease: SMOOTH },
    { at: 0.19, values: [1.045, 0.955], ease: SMOOTH },
    { at: 0.32, values: [0.98, 1.03], ease: SMOOTH },
    { at: 0.41, values: [1, 1] },
    { at: 0.5, values: [1, 1], ease: SMOOTH },
    { at: 0.63, values: [1.045, 0.955], ease: SMOOTH },
    { at: 0.76, values: [0.98, 1.03], ease: SMOOTH },
    { at: 0.85, values: [1, 1] },
    { at: 1, values: [1, 1] },
  ],
  /** Amber card: [translateY]. */
  aY: [
    { at: 0, values: [0] },
    { at: 0.06, values: [0], ease: TRAVEL },
    { at: 0.32, values: [SWAP] },
    { at: 0.5, values: [SWAP], ease: TRAVEL },
    { at: 0.76, values: [0] },
    { at: 1, values: [0] },
  ],
  /** Amber card: [translateX] — bows right going down, left coming back. */
  aX: [
    { at: 0, values: [0] },
    { at: 0.06, values: [0], ease: SWING_OUT },
    { at: 0.19, values: [0.7], ease: SWING_IN },
    { at: 0.32, values: [0] },
    { at: 0.5, values: [0], ease: SWING_OUT },
    { at: 0.63, values: [-1.3], ease: SWING_IN },
    { at: 0.76, values: [0] },
    { at: 1, values: [0] },
  ],
  /** Amber card: [scale] — swells as it passes in front. */
  aS: [
    { at: 0, values: [1] },
    { at: 0.06, values: [1], ease: SMOOTH },
    { at: 0.19, values: [1.06], ease: SMOOTH },
    { at: 0.32, values: [1] },
    { at: 0.5, values: [1], ease: SMOOTH },
    { at: 0.63, values: [1.06], ease: SMOOTH },
    { at: 0.76, values: [1] },
    { at: 1, values: [1] },
  ],
  /** Violet card: [translateY]. */
  bY: [
    { at: 0, values: [0] },
    { at: 0.06, values: [0], ease: TRAVEL },
    { at: 0.32, values: [-SWAP] },
    { at: 0.5, values: [-SWAP], ease: TRAVEL },
    { at: 0.76, values: [0] },
    { at: 1, values: [0] },
  ],
  /** Violet card: [translateX] — bows the other way. */
  bX: [
    { at: 0, values: [0] },
    { at: 0.06, values: [0], ease: SWING_OUT },
    { at: 0.19, values: [-1.3], ease: SWING_IN },
    { at: 0.32, values: [0] },
    { at: 0.5, values: [0], ease: SWING_OUT },
    { at: 0.63, values: [0.7], ease: SWING_IN },
    { at: 0.76, values: [0] },
    { at: 1, values: [0] },
  ],
  /** Violet card: [scale] — shrinks as it passes behind. */
  bS: [
    { at: 0, values: [1] },
    { at: 0.06, values: [1], ease: SMOOTH },
    { at: 0.19, values: [0.94], ease: SMOOTH },
    { at: 0.32, values: [1] },
    { at: 0.5, values: [1], ease: SMOOTH },
    { at: 0.63, values: [0.94], ease: SMOOTH },
    { at: 0.76, values: [1] },
    { at: 1, values: [1] },
  ],
} as const satisfies Record<string, LogoTrack>;

export type LogoTrackName = keyof typeof LOGO_LOADER_TRACKS;

/**
 * Reduce Motion: nothing moves; the two cards dim one after the other, slowly,
 * so the wait still reads as alive (a frozen mark reads as a hung page).
 */
export const LOGO_LOADER_REDUCED_DURATION_MS = 3200;
export const LOGO_LOADER_REDUCED_TRACKS = {
  /** Amber card: [opacity]. */
  a: [
    { at: 0, values: [1], ease: SMOOTH },
    { at: 0.25, values: [0.55], ease: SMOOTH },
    { at: 0.5, values: [1] },
    { at: 1, values: [1] },
  ],
  /** Violet card: [opacity]. */
  b: [
    { at: 0, values: [1] },
    { at: 0.5, values: [1], ease: SMOOTH },
    { at: 0.75, values: [0.55], ease: SMOOTH },
    { at: 1, values: [1] },
  ],
} as const satisfies Record<string, LogoTrack>;

/**
 * The label's ellipsis: three dots that appear in turn and fade together, in a
 * fixed-width box so the text never shifts. Reduce Motion shows all three.
 */
export const LOGO_LOADER_DOTS_DURATION_MS = 1600;
export const LOGO_LOADER_DOT_TRACKS = [
  [
    { at: 0, values: [0], ease: SWING_OUT },
    { at: 0.1, values: [1] },
    { at: 0.75, values: [1], ease: SMOOTH },
    { at: 0.9, values: [0] },
    { at: 1, values: [0] },
  ],
  [
    { at: 0, values: [0] },
    { at: 0.25, values: [0], ease: SWING_OUT },
    { at: 0.35, values: [1] },
    { at: 0.75, values: [1], ease: SMOOTH },
    { at: 0.9, values: [0] },
    { at: 1, values: [0] },
  ],
  [
    { at: 0, values: [0] },
    { at: 0.5, values: [0], ease: SWING_OUT },
    { at: 0.6, values: [1] },
    { at: 0.75, values: [1], ease: SMOOTH },
    { at: 0.9, values: [0] },
    { at: 1, values: [0] },
  ],
] as const satisfies readonly LogoTrack[];

/** Solve a CSS cubic-bezier for y at progress x (Newton, then bisection). */
export function cubicBezierAt(ease: LogoEase, x: number): number {
  'worklet';
  const [x1, y1, x2, y2] = ease;
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  let t = x;
  for (let i = 0; i < 8; i++) {
    const err = ((ax * t + bx) * t + cx) * t - x;
    if (Math.abs(err) < 1e-6) break;
    const d = (3 * ax * t + 2 * bx) * t + cx;
    if (Math.abs(d) < 1e-6) break;
    t -= err / d;
  }
  if (t < 0 || t > 1 || Math.abs(((ax * t + bx) * t + cx) * t - x) > 1e-5) {
    let lo = 0;
    let hi = 1;
    t = x;
    for (let i = 0; i < 30; i++) {
      const v = ((ax * t + bx) * t + cx) * t;
      if (Math.abs(v - x) < 1e-6) break;
      if (v < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
  }
  return ((ay * t + by) * t + cy) * t;
}

/**
 * The value of channel `channel` of `track` at loop progress `p` (0–1), the
 * way a CSS keyframe animation would draw it.
 */
export function sampleLogoTrack(track: LogoTrack, p: number, channel = 0): number {
  'worklet';
  const n = track.length;
  if (p <= track[0]!.at) return track[0]!.values[channel]!;
  for (let i = 0; i < n - 1; i++) {
    const from = track[i]!;
    const to = track[i + 1]!;
    if (p <= to.at) {
      const span = to.at - from.at;
      const local = span <= 0 ? 1 : (p - from.at) / span;
      const eased = cubicBezierAt(from.ease ?? LOGO_EASE_DEFAULT, local);
      const a = from.values[channel]!;
      const b = to.values[channel]!;
      return a + (b - a) * eased;
    }
  }
  return track[n - 1]!.values[channel]!;
}
